// Error tracking for the dashboard (Sentry project "vaartix-app").
//
// Off unless VITE_SENTRY_DSN was set when the dashboard was built (Cloudflare Pages → vaartix-app →
// Variables). No session replay, no tracing, no sessions; every event and breadcrumb goes through
// the same allowlist as the backend's (@vaartix/shared/scrub), so Sentry never sees email
// addresses, codes, tokens, keys or anything a page showed.

import * as Sentry from '@sentry/react';
import { NO_DATA_COLLECTION, scrubBreadcrumb, scrubEvent } from '@vaartix/shared/scrub';
import { ApiError } from './api';

const DSN = (import.meta.env.VITE_SENTRY_DSN as string | undefined) || undefined;

/** Default integrations left out: session pings and AI conversation IDs. */
const DROPPED_INTEGRATIONS = new Set(['BrowserSession', 'ConversationId']);

export function initSentry() {
  if (!DSN) return;
  Sentry.init({
    dsn: DSN,
    environment: import.meta.env.DEV ? 'development' : 'production',
    ...(__VAARTIX_RELEASE__ ? { release: __VAARTIX_RELEASE__ } : {}),
    // Collect nothing about the user or requests (v11's replacement for sendDefaultPii: false).
    dataCollection: { ...NO_DATA_COLLECTION, httpBodies: [...NO_DATA_COLLECTION.httpBodies] },
    integrations: (defaults) => defaults.filter((i) => !DROPPED_INTEGRATIONS.has(i.name)),
    maxBreadcrumbs: 30,
    beforeSend(event, hint) {
      // An API error the page already showed (validation, auth, rate limit, captcha) isn't a bug.
      const error = hint.originalException;
      if (error instanceof ApiError && error.status < 500) return null;
      return scrubEvent(event);
    },
    beforeBreadcrumb: (breadcrumb) => scrubBreadcrumb(breadcrumb),
  });
}

/** Report an error that a page caught, such as one shown by a route's error screen. */
export function reportError(error: unknown) {
  if (DSN) Sentry.captureException(error);
}
